import React from 'react';
import { TrackedLink } from 'components/common/TrackedLink';
import { basePaths } from 'app-constants';
import { useQueryAndHash } from './useQueryAndHash';
import { LinkHelperProps } from './index';

type CourseAction = 'play' | 'evaluate' | 'search' | undefined;
type CourseMode = 'raw' | undefined;

interface CourseLinkProps extends LinkHelperProps {
  slug?: string;
  action?: CourseAction;
  mode?: CourseMode;
}

export type { CourseLinkProps };

export function parseCoursePath(path: string) {
  // Parse course paths like /learning/courses/{id}
  const coursePathRegex = new RegExp(`^${basePaths.courses}/([^/]+)$`);
  const match = path.match(coursePathRegex);
  if (match) {
    return {
      slug: match[1],
    };
  }
  return null;
}

export function useCoursePath(
  slug?: string,
  action?: CourseAction,
  mode?: CourseMode,
  query?: string | null,
  hash?: string | null,
) {
  const path = React.useMemo(() => {
    const parts = [basePaths.courses];

    if (slug) {
      parts.push(slug);
    }

    if (action) {
      parts.push(action);
    }

    if (mode === 'raw') {
      parts.unshift('raw');
    }

    return parts.join('/').replace(/\/+/g, '/');
  }, [slug, action, mode]);

  return useQueryAndHash(path, query, hash);
}

export function CourseLink({
  to,
  slug,
  action,
  mode,
  query,
  hash,
  ...props
}: CourseLinkProps) {
  const path = useCoursePath(slug, action, mode, query);
  return <TrackedLink to={to ? to : path} {...props} />;
}
